import {
  MadFullFragment,
  MadReportFullFragment,
  ReportAnswerFragment,
  ReportFragment,
  UserMinimalFragment,
} from "../graphql/fragments";
import { TemplateQuestionType } from "../graphql/types";
import { compareAsc, differenceInMonths, isBefore } from "date-fns";

/** Extract from an array of reports every answers that answers a TemplateQuestion of the passed type */
export function extractReportAnswersType(
  reports: (ReportFragment | MadReportFullFragment)[],
  type: TemplateQuestionType
): ReportAnswerFragment[] {
  return reports
    .flatMap((report) => {
      return report?.reportAnswers.filter((ra) => {
        return ra.templateQuestion.type === type;
      });
    })
    .filter((ra) => ra !== undefined);
}

/**
 * Get last mad report
 * Note: This function only take into account checked reports
 */
export function getLastReport(mad: MadFullFragment): MadReportFullFragment {
  let last = mad.reports?.[0];

  mad.reports.forEach((report) => {
    if (
      report.checkedAt &&
      isBefore(new Date(last.checkedAt), new Date(report.checkedAt))
    )
      last = report;
  });

  return last;
}

type OverdueReport = {
  report: MadReportFullFragment;
  reason: "talent_overdue" | "referent_overdue";
  concernedUsers: UserMinimalFragment[];
};

/**
 * Give back overdue reports as well as usef informations to identify where the problem is
 */
export function getOverdueReports(mad: MadFullFragment): OverdueReport[] {
  const overdues: OverdueReport[] = [];

  const isOverdue = (report) => {
    if (
      !report?.filledAt &&
      (isBefore(new Date(mad.nextReportAt), new Date(report?.createdAt)) ||
        differenceInMonths(new Date(), new Date(report?.createdAt)) >= 1)
    )
      overdues.push({
        report,
        reason: "talent_overdue",
        concernedUsers: [mad.talent],
      });
    else if (
      !report?.checkedAt &&
      (isBefore(new Date(mad.nextReportAt), new Date(report?.filledAt)) ||
        differenceInMonths(new Date(), new Date(report?.createdAt)) >= 1)
    )
      overdues.push({
        report,
        reason: "referent_overdue",
        concernedUsers: [...report.reportReferents].map((rr) => rr.referent),
      });
  };

  if (mad.reports.length === 0) isOverdue(undefined);
  mad.reports.forEach((report) => isOverdue(report));

  return overdues;
}

export type ReportQuestionAverage = {
  templateQuestionDigest: string;
  reportAnswers: ReportAnswerFragment[];
  averageGrade: number;
  averageGradeLabel?: string;
  averageTimeSpent?: number;
  gradeCount: number;
  timeSpentCount?: number;
};

export function getReportStatus(
  mad: MadFullFragment,
  report?: MadReportFullFragment
) {
  if (isBefore(new Date(), new Date(mad.startDate))) return "not_started";

  if (!report?.filledAt && isBefore(new Date(mad.startDate), new Date()))
    return "awaiting_talent";
  if (
    mad.schedule === null &&
    (!mad.endDate || report.type === "FinalReport") &&
    report.checkedAt
  )
    return "mad_ended";
  if (report?.checkedAt) return "report_ready";
  if (report?.filledAt && mad.referents.length > 0) return "awaiting_referents";
}

/**
 * Return the averaged grade of a group of reportAnswers as well as other useful informations
 * Note: it will purge every answers that are not either of type 'TemplateQuestionSkill' or 'TemplateQuestionEvaluation'
 */
export function getReferentGradeAverage(
  reportAnswers: ReportAnswerFragment[],
  referentsIds: string[]
) {
  const average: ReportQuestionAverage[] = [];

  reportAnswers.forEach((ra) => {
    if (ra.type !== "ReportAnswerSkill" && ra.type !== "ReportAnswerEvaluation")
      return;

    const current = average.find(
      (a) => a.templateQuestionDigest === ra.templateQuestion.digest
    );

    if (current && referentsIds.includes(ra.user.id)) {
      current.gradeCount += 1;
      current.averageGrade += parseInt(ra.value.selector as string, 10);
      current.reportAnswers.push(ra);
      if (ra.timeSpent) {
        current.timeSpentCount += 1;
        current.averageTimeSpent += ra.timeSpent;
      }
    } else if (referentsIds.includes(ra.user.id)) {
      average.push({
        templateQuestionDigest: ra.templateQuestion.digest,
        averageGrade: parseInt(ra.value.selector as string, 10),
        reportAnswers: [ra],
        gradeCount: 1,
        averageTimeSpent: ra.timeSpent,
        timeSpentCount: ra.timeSpent ? 1 : 0,
      });
    }
  });

  average.forEach((e) => {
    e.averageGrade /= e.gradeCount;

    const currentOption = e.reportAnswers[0].templateQuestion?.options.find(
      (o) => parseInt(o.value) === Math.floor(e.averageGrade)
    );
    e.averageGradeLabel = currentOption ? currentOption.label : "Unknown option";
    e.averageTimeSpent /= e.timeSpentCount;
  });

  return average;
}

export function getTalentUserData(reportAnswers: ReportAnswerFragment[]) {
  const talentData: {
    user: UserMinimalFragment;
    reports: {
      id: string;
      date: string;
      reportAnswers: ReportAnswerFragment[];
    }[];
  }[] = [];

  // First loop to create every talent & reports entries
  reportAnswers.forEach((ra) => {
    if (!ra.isTalent) return;

    const talent = talentData.find(({ user }) => ra.user.id === user.id);
    const isReportDup = !!talentData.find(({ reports }) =>
      reports.find((report) => report.id === ra.reportId)
    );

    if (isReportDup) return;
    if (!talent)
      talentData.push({
        user: ra.user,
        reports: [
          { id: ra.reportId, date: ra.report.createdAt, reportAnswers: [] },
        ],
      });
    else
      talent.reports.push({
        id: ra.reportId,
        date: ra.report.createdAt,
        reportAnswers: [],
      });
  });

  // Append to each reports all the reportAnswers
  reportAnswers.forEach((ra) => {
    const talent = talentData.find(({ reports }) =>
      reports.find((report) => report.id === ra.reportId)
    );

    if (talent) {
      const report = talent.reports.find(({ id }) => id === ra.reportId);

      if (report) {
        const dup = report.reportAnswers.find(({ id }) => id === ra.id);

        if (!dup) report.reportAnswers.push(ra);
      }
    }
  });

  // Ordering them by date ASC
  talentData.forEach(({ reports }) => {
    reports.sort((i, j) => compareAsc(new Date(i.date), new Date(j.date)));
    reports.forEach(({ reportAnswers }) =>
      reportAnswers.sort((i, j) =>
        compareAsc(new Date(i.updatedAt), new Date(j.updatedAt))
      )
    );
  });

  return talentData;
}
